import { useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminPasswordGenerator({ config }) {
  const [passwords, setPasswords] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [count, setCount] = useState(10);
  const [validDays, setValidDays] = useState(30);
  const [promoName, setPromoName] = useState("");

  const generatePasswords = async () => {
    setGenerating(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/generate-passwords`,
        {
          count: parseInt(count),
          valid_days: parseInt(validDays),
          promo_name: promoName || "Monthly Promotion"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPasswords(response.data.passwords);
    } catch (err) {
      alert(err.response?.data?.detail || "Error generating passwords");
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const copyAllPasswords = () => {
    const allPasswords = passwords.map(p => p.password).join("\n");
    navigator.clipboard.writeText(allPasswords);
    alert("All passwords copied to clipboard!");
  };

  const downloadCSV = () => {
    const csv = [
      "Password,Expires,Promo,Status",
      ...passwords.map(p => `${p.password},${p.expires_at},${p.promo_name},Active`)
    ].join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `passwords-${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="card" data-testid="admin-password-generator">
      <h3>🔑 Password Generator</h3>
      <p style={{ color: "#FFD700", marginBottom: "20px" }}>
        Generate temporary passwords for promotions and special access
      </p>

      {/* Generation Form */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "15px",
        marginBottom: "20px"
      }}>
        <div>
          <label style={{ display: "block", marginBottom: "5px", color: "#FFD700" }}>
            Number of Passwords
          </label>
          <input
            type="number"
            min="1"
            max="100"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>

        <div>
          <label style={{ display: "block", marginBottom: "5px", color: "#FFD700" }}>
            Valid for (days)
          </label>
          <input
            type="number"
            min="1"
            max="365"
            value={validDays}
            onChange={(e) => setValidDays(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>

        <div>
          <label style={{ display: "block", marginBottom: "5px", color: "#FFD700" }}>
            Promotion Name
          </label>
          <input
            type="text"
            value={promoName}
            onChange={(e) => setPromoName(e.target.value)}
            placeholder="e.g., January Special"
            style={{ width: "100%" }}
          />
        </div>
      </div>

      <button
        onClick={generatePasswords}
        disabled={generating}
        style={{ marginBottom: "20px" }}
      >
        {generating ? "Generating..." : "🎯 Generate Passwords"}
      </button>

      {/* Generated Passwords */}
      {passwords.length > 0 && (
        <div>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px"
          }}>
            <h4 style={{ color: "#FFD700", margin: 0 }}>
              Generated {passwords.length} Passwords
            </h4>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={copyAllPasswords}
                style={{ width: "auto", padding: "8px 15px", fontSize: "0.9rem" }}
              >
                📋 Copy All
              </button>
              <button
                onClick={downloadCSV}
                style={{ width: "auto", padding: "8px 15px", fontSize: "0.9rem" }}
              >
                📥 Download CSV
              </button>
            </div>
          </div>

          <div style={{
            background: "#0a0a0a",
            padding: "15px",
            borderRadius: "6px",
            maxHeight: "400px",
            overflowY: "auto"
          }}>
            <table style={{ width: "100%", fontSize: "0.9rem" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>#</th>
                  <th style={{ textAlign: "left" }}>Password</th>
                  <th style={{ textAlign: "left" }}>Expires</th>
                  <th style={{ textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {passwords.map((pwd, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <code style={{
                        background: "#222",
                        padding: "5px 10px",
                        borderRadius: "4px",
                        color: "#90EE90",
                        fontFamily: "monospace"
                      }}>
                        {pwd.password}
                      </code>
                    </td>
                    <td style={{ fontSize: "0.85rem", color: "#aaa" }}>
                      {new Date(pwd.expires_at).toLocaleDateString()}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        onClick={() => copyToClipboard(pwd.password)}
                        style={{
                          width: "auto",
                          padding: "5px 15px",
                          fontSize: "0.8rem",
                          marginTop: 0
                        }}
                      >
                        Copy
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{
            marginTop: "15px",
            padding: "15px",
            background: "#1a3a4d",
            borderRadius: "6px",
            border: "1px solid #87CEEB"
          }}>
            <p style={{ margin: "0 0 5px 0", fontSize: "0.9rem", color: "#87CEEB" }}>
              <strong>Usage Instructions:</strong>
            </p>
            <p style={{ margin: 0, fontSize: "0.85rem", color: "#eee" }}>
              Share these passwords with promotional users. They can register using these as temporary 
              passwords, then change them after first login. Passwords expire in {validDays} days.
            </p>
          </div>
        </div>
      )}

      {/* Info */}
      <div style={{
        marginTop: "20px",
        padding: "15px",
        background: "#1a1a1a",
        borderRadius: "6px"
      }}>
        <h4 style={{ color: "#FFD700", marginBottom: "10px" }}>
          💡 Use Cases
        </h4>
        <ul style={{ margin: 0, paddingLeft: "20px", color: "#aaa", fontSize: "0.9rem" }}>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: 0 }}>
            <strong style={{ color: "#eee" }}>Monthly Promotions:</strong> Give free trial access
          </li>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: 0 }}>
            <strong style={{ color: "#eee" }}>Special Events:</strong> Conference attendee access
          </li>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: 0 }}>
            <strong style={{ color: "#eee" }}>Community Outreach:</strong> Limited-time invitations
          </li>
          <li style={{ background: "transparent", border: "none", padding: 0 }}>
            <strong style={{ color: "#eee" }}>Bulk Registration:</strong> Pre-generate for groups
          </li>
        </ul>
      </div>
    </div>
  );
}

export default AdminPasswordGenerator;
